import { Link } from 'react-router-dom';
import { ArrowRight, Trophy } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CompTag } from '@/components/CompTag';
import { Countdown } from '@/components/Countdown';
import { ImgPlaceholder } from '@/components/ImgPlaceholder';
import { SiteHeader } from '@/components/SiteHeader';
import { useCompetitions } from '@/hooks/useCompetitions';
import { formatDate } from '@/lib/utils';

export function BrowsePage() {
	const { data: competitions, isLoading, error } = useCompetitions();

	const featured = competitions?.find((c) => c.Status === 'open');
	const rest = competitions?.filter((c) => c.Id !== featured?.Id) ?? [];

	return (
		<>
			<SiteHeader />
			<main className="mx-auto max-w-6xl space-y-10 px-5 py-10">
				<header className="flex items-center gap-3">
					<Trophy className="h-7 w-7 text-loomlite-purple-soft" />
					<div>
						<h1 className="m-0 font-heading text-3xl font-bold">Loomlite Competitions</h1>
						<p className="m-0 mt-1 text-sm text-loomlite-muted">
							TTRPG showcases for Game Masters · open calls, ongoing judging, and announced winners.
						</p>
					</div>
				</header>

				{isLoading && <p className="text-loomlite-muted">Loading…</p>}
				{error && <p className="text-red-400">Failed to load competitions.</p>}
				{competitions && competitions.length === 0 && (
					<p className="text-loomlite-muted">No competitions right now. Check back soon.</p>
				)}

				{featured && (
					<Card className="overflow-hidden border-loomlite-gold/35 bg-[linear-gradient(180deg,rgba(255,140,0,0.06)_0%,transparent_50%),var(--color-loomlite-card)]">
						<div className="grid gap-0 md:grid-cols-[1.1fr_1fr]">
							<div className="p-7">
								<CompTag color="gold">Featured</CompTag>
								<h2 className="m-0 mt-4 font-heading text-[34px] font-bold leading-tight tracking-tight">
									<span className="ll-gradient-text">{featured.Name}</span>
								</h2>
								{featured.Description && (
									<p className="m-0 mt-3 line-clamp-3 text-loomlite-muted">
										{featured.Description}
									</p>
								)}
								<div className="mt-5 flex flex-wrap items-center gap-3">
									<Countdown compact targetIso={featured.SubmissionCloseAt} />
									<Badge variant="muted">Closes {formatDate(featured.SubmissionCloseAt)}</Badge>
								</div>
								<div className="mt-6 flex gap-2">
									<Button asChild>
										<Link to={`/competitions/${featured.Slug}/submit`}>Submit an entry</Link>
									</Button>
									<Button asChild variant="outline">
										<Link to={`/competitions/${featured.Slug}`}>
											Details <ArrowRight className="ml-1 h-4 w-4" />
										</Link>
									</Button>
								</div>
							</div>
							<div className="p-5 md:p-7">
								<ImgPlaceholder label="competition banner" height={220} tone="gold" />
							</div>
						</div>
					</Card>
				)}

				{rest.length > 0 && (
					<section className="space-y-4">
						<h3 className="font-heading text-lg font-semibold">All competitions</h3>
						<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
							{rest.map((c) => (
								<Card key={c.Id} className="flex flex-col">
									<CardHeader>
										<div className="flex items-start justify-between gap-2">
											<CardTitle>{c.Name}</CardTitle>
											<Badge
												variant={
													c.Status === 'open'
														? 'success'
														: c.Status === 'announced'
															? 'gold'
															: 'muted'
												}
											>
												{c.Status}
											</Badge>
										</div>
										<CardDescription className="line-clamp-3">{c.Description}</CardDescription>
									</CardHeader>
									<CardContent className="mt-auto space-y-3">
										<p className="text-xs text-loomlite-dim">
											{c.Status === 'open'
												? `Closes ${formatDate(c.SubmissionCloseAt)}`
												: c.Status === 'announced'
													? `Announced ${formatDate(c.AnnouncementAt ?? c.SubmissionCloseAt)}`
													: `Closed ${formatDate(c.SubmissionCloseAt)}`}
										</p>
										<Button asChild variant="outline" size="sm" className="w-full">
											<Link to={`/competitions/${c.Slug}`}>
												View details <ArrowRight className="ml-1 h-4 w-4" />
											</Link>
										</Button>
									</CardContent>
								</Card>
							))}
						</div>
					</section>
				)}
			</main>
		</>
	);
}
